import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Mic, Pause, Play, RotateCcw, Square, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#D7BB7A";
const CREAM = "#F1ECDD";
const MAX_S = 90;
const pickMime = () => ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const ERR = {
  audio_silent: "I couldn't hear anything. Try again a little closer to the phone.",
  audio_too_small: "That was a bit short. Say a few words and try again.",
};

// Sender side: record a short personal message that plays before the installation. Uploads as soon as you stop.
export const GiftVoiceRecorder = ({ journeyId, onNote }) => {
  const [state, setState] = useState("idle"); // idle | recording | uploading | ready | error
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState("");
  const [url, setUrl] = useState("");
  const [playing, setPlaying] = useState(false);
  const rec = useRef(null);
  const timer = useRef(null);
  const player = useRef(null);
  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const upload = async (blob) => {
    setState("uploading");
    const fd = new FormData();
    fd.append("audio", blob, blob.type.includes("mp4") ? "note.m4a" : "note.webm");
    try {
      const r = await axios.post(`${API}/api/audio-journeys/${journeyId}/gift-note`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
      setUrl(`${API}/api/public/share/${r.data.slug}/note/${r.data.note_id}`);
      onNote(r.data.note_id);
      setState("ready");
    } catch (e) {
      setErr(ERR[e?.response?.data?.detail] || "That didn't save. Please try again.");
      setState("error");
    }
  };
  const start = async () => {
    setErr(""); player.current?.pause(); onNote(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); upload(new Blob(chunks, { type: r.mimeType || mime || "audio/webm" })); };
      rec.current = r; r.start(); setSecs(0); setState("recording");
      timer.current = setInterval(() => setSecs((s) => { if (s + 1 >= MAX_S) stop(); return s + 1; }), 1000);
    } catch { setErr("Please allow the microphone to record a message."); setState("error"); }
  };
  const stop = () => { clearInterval(timer.current); if (rec.current?.state === "recording") rec.current.stop(); };
  const remove = () => { onNote(null); setUrl(""); setState("idle"); };
  const toggle = () => { const a = player.current; if (!a) return; if (playing) a.pause(); else a.play().catch(() => {}); };

  const btn = "rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 transition-transform duration-200 active:scale-95";
  return (
    <div className="rounded-2xl p-4" style={{ border: "1px solid rgba(215,187,122,0.25)", background: "rgba(241,236,221,0.03)" }} data-testid="gift-voice">
      <p className="text-[0.6rem] uppercase tracking-[0.26em]" style={{ color: GOLD }}>Add a voice message · optional</p>
      {state === "idle" && <p className="mt-1 text-sm" style={{ color: CREAM, opacity: 0.75 }}>Say a few words in your own voice. They'll hear it first, before the installation starts.</p>}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {(state === "idle" || state === "error") && (
          <button type="button" onClick={start} className={btn} style={{ background: GOLD, color: "#0a132a" }} data-testid="gift-voice-record"><Mic size={14} /> Record message</button>
        )}
        {state === "recording" && (
          <button type="button" onClick={stop} className={btn} style={{ background: "#e9a39c", color: "#0a132a" }} data-testid="gift-voice-stop">
            <Square size={13} /> Stop · {secs}s
          </button>
        )}
        {state === "uploading" && <span className="inline-flex items-center gap-2 text-sm" style={{ color: CREAM }}><Loader2 size={14} className="animate-spin" /> Saving your message…</span>}
        {state === "ready" && (
          <>
            <button type="button" onClick={toggle} className={btn} style={{ border: `1px solid ${GOLD}`, color: GOLD }} data-testid="gift-voice-play">
              {playing ? <Pause size={13} /> : <Play size={13} />} {playing ? "Pause" : "Listen back"}
            </button>
            <button type="button" onClick={start} className={btn} style={{ color: CREAM, opacity: 0.8 }} data-testid="gift-voice-redo"><RotateCcw size={13} /> Redo</button>
            <button type="button" onClick={remove} aria-label="Remove message" className="p-2" style={{ color: CREAM, opacity: 0.6 }} data-testid="gift-voice-remove"><X size={15} /></button>
            <audio ref={player} src={url} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
          </>
        )}
      </div>
      {state === "ready" && <p className="mt-2 text-xs" style={{ color: GOLD }} data-testid="gift-voice-ready">Your message is attached.</p>}
      {err && <p className="mt-2 text-xs" style={{ color: "#e9a39c" }} data-testid="gift-voice-error">{err}</p>}
    </div>
  );
};

// Recipient side: the sender's message, played before the installation.
export const GiftVoiceNote = ({ slug, noteId, from, onFinished, onStart, gold = GOLD, cream = CREAM }) => {
  const [state, setState] = useState("idle"); // idle | playing | done | missing
  const ref = useRef(null);
  if (state === "missing") return null;
  const play = () => { onStart?.(); ref.current?.play().then(() => setState("playing")).catch(() => {}); };
  return (
    <div className="rounded-2xl p-5 mb-6 text-center" style={{ border: `1px solid ${gold}`, background: "rgba(215,187,122,0.08)" }} data-testid="gift-voice-note">
      <p className="text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: gold, fontFamily: '"Manrope", sans-serif' }}>
        A voice message{from ? ` from ${from}` : ""}
      </p>
      <p className="mt-2 text-lg" style={{ color: cream, fontFamily: '"Cormorant Garamond", serif' }}>
        {state === "done" ? "Now, when you're ready, press play on your installation below." : "Listen to this first."}
      </p>
      {state !== "done" && (
        <button type="button" onClick={state === "playing" ? () => { ref.current.pause(); setState("idle"); } : play} data-testid="gift-voice-note-play"
          className="mt-4 rounded-full px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold inline-flex items-center gap-2 transition-transform duration-200 active:scale-95"
          style={{ background: gold, color: "#0a132a" }}>
          {state === "playing" ? <Pause size={14} /> : <Play size={14} />} {state === "playing" ? "Pause" : "Play message"}
        </button>
      )}
      <audio ref={ref} src={`${API}/api/public/share/${slug}/note/${noteId}`} preload="auto" onError={() => setState("missing")} onEnded={() => { setState("done"); onFinished?.(); }} data-testid="gift-voice-note-audio" />
    </div>
  );
};
